import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import '../css/ProjectPage.css';
import '../css/Buttons.css';

import IndexListItem from '../components/IndexListItem';
import SectionCover from '../components/SectionCover';
import TextSlide from '../components/TextSlide';
import VideoSlide from '../components/VideoSlide';
import ColumnTextSlide from '../components/ColumnTextSlide';

import FloatingNavigation from '../components/FloatingNavigation';

import ImageSlide from '../components/ImageSlide';

import buxFullOnboardingFLow from '../assets/bux-full-onboarding flow.webp';

import buxSignupChurn from '../assets/bux-signup-churn.webp';

import buxSignupFlow from '../assets/bux-signup-flow.webp';

import buxWelcomeScreensAbTests from '../assets/bux-welcome-screen-ab-tests.webp';

import buxUxCopyChanges from '../assets/bux-copy-changes.webp';

import buxBenchmarkProgress from '../assets/bux-benchmark-progress.webp';

import buxProgressInterstitial from '../assets/bux-progress-feedback-interstitial.webp';

import buxProgressReturn from '../assets/bux-progress-feedback-return.webp';

import buxWebOnboardingFlow from '../assets/bux-web-onboarding-flow.webp';

import buxWebOnboardingWireframes from '../assets/bux-web-onboarding-wireframes.webp';

import buxWebOnboardingPrototype from '../assets/bux-web-onboarding-prototype.webp';

import buxWebOnboardingQrCode from '../assets/bux-web-onboarding-qr-code.webp';

import buxWebOnboardingLibrary from '../assets/bux-web-onboarding-library.webp';

import buxWebOnboardingFinalProduct from '../assets/bux-web-onboarding-final-product.webp';



import buxShorterFlow from '../assets/bux-shorter-flow.webp';



const ProjectBuxZero = () => {

  

  const sections = useMemo(() => [
    { id: 'project-background', label: 'Project background' },
    { id: 'signup-optimization', label: 'Signup optimization' },
    { id: 'progress-feedback', label: 'Progress feedback' },
    { id: 'web-onboarding', label: 'Web onboarding' },
  ], []);

  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [showFloatingButtons, setShowFloatingButtons] = useState(false);

  // Smooth scroll to a section
  const scrollToElement = (selector) => {
    const element = document.querySelector(selector);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      console.warn(`Element not found for selector: ${selector}`);
    }
  };

  // Handle scroll and update visibility/index
  const sectionsRef = useRef(sections);
  const currentIndexRef = useRef(0);

  useEffect(() => {
    sectionsRef.current = sections;
  }, [sections]);

  useEffect(() => {
    currentIndexRef.current = currentSectionIndex;
  }, [currentSectionIndex]);

  const handleScroll = useCallback(() => {
    const firstSection = document.getElementById('project-background');
    const firstSectionTop = firstSection ? firstSection.getBoundingClientRect().top : 0;
    setShowFloatingButtons(firstSectionTop <= 0);

    const offsets = sectionsRef.current.map((section) => {
      const el = document.getElementById(section.id);
      return el ? el.getBoundingClientRect().top : Infinity;
    });

    const closestIndex = offsets.findIndex((offset) => offset >= -10);
    if (closestIndex !== -1 && closestIndex !== currentIndexRef.current) {
      setCurrentSectionIndex(closestIndex);
    }
  }, []);

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'auto';
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  return (
    <div className="project-body">
      {/* Header */}
      <div className="project-header">
        
        <h2>BUX Zero</h2>
        <div className="project-header-image-bux"></div>
        
      </div>

      {/* Index Section */}
      <section className="project-index">
        <div className="index-wrapper">
          {sections.map((section, index) => (
            <IndexListItem
              key={section.id}
              itemNumber={index + 1}
              label={section.label}
              onClick={() => {
                // When index item is clicked, scroll to the corresponding section
                scrollToElement(`#${section.id}`);
              }}
            />
          ))}
        </div>
      </section>

      {/* Sections */}

      {/* Section 01 */}
      <div id="project-background">
        
        <SectionCover sectionNumber="1" sectionTitle="Project background" />

        <ImageSlide 
          imageSlideImage={buxFullOnboardingFLow}
          imageSlideTitle="Overview of the onboarding flow"
          showTitle={true}       // Title is shown
          showImage={true}      // Image is hidden
          showCaption={false}     // Caption is shown
          showAnimation={false}
        />

        <TextSlide
          slideTitle="Goals"
          slideText={[
            'Reduce churn during sign-ups',
            'Optimise flow to improve number of accounts created',
            'Start onboarding users on web',
          ]}
        />

      </div>

      
      {/* Section 02 */}

      <div id="signup-optimization">

        <SectionCover sectionNumber="2" sectionTitle="Sign-up optimization" />

          <ImageSlide 
            imageSlideImage={buxSignupChurn}
            imageSlideTitle="An analysis of the flow indicated where most customers interrupted their account creation"
            imageSlideCaption="Drop-off rate of users during account creation"
            showTitle={true}       // Title is shown
            showImage={true}      // Image is hidden
            showCaption={true}     // Caption is shown
            showAnimation={false}
          />

          <ImageSlide 
            imageSlideImage={buxSignupFlow}
            imageSlideTitle="We chose to start experimenting with the sign-up flow, where we had more flexibility"
            imageSlideCaption="Screens in the sign-up flow"
            showTitle={true}       // Title is shown
            showImage={true}      // Image is hidden
            showCaption={true}     // Caption is shown
            showAnimation={false}
          />

          

        <TextSlide
          slideTitle="Strategic changes"
          slideText={[
            'Continuous A/B test of welcome screen',
            'Shorten sign-up flow',
            'Review all copy',
          ]}
        />

        <ImageSlide 
          imageSlideImage={buxWelcomeScreensAbTests}
          imageSlideTitle="Continuous A/B test of welcome screen"
          showTitle={true}       // Title is shown
          showImage={true}      // Image is hidden
          showCaption={false}     // Caption is shown
          showAnimation={false}
        />

        <ImageSlide 
          imageSlideImage={buxShorterFlow}
          imageSlideTitle="Excluding, merging and improving screens in the sign-up flow increased sign-ups by nearly 7%"
          showTitle={true}       // Title is shown
          showImage={true}      // Image is hidden
          showCaption={false}     // Caption is shown
          showAnimation={false}
        />

        <ImageSlide 
          imageSlideImage={buxUxCopyChanges}
          imageSlideTitle="UX copy reviewed on 102 screens"
          imageSlideCaption="Example of UX copy update"
          showTitle={true}       // Title is shown
          showImage={true}      // Image is hidden
          showCaption={true}     // Caption is shown
          showAnimation={false}
        />

         

        
      </div>

      


      {/* Section 03 */}

      <div id="progress-feedback">
        <SectionCover sectionNumber="3" sectionTitle="Progress feedback" />

        <TextSlide
          slideTitle="Problem identification"
          slideText={[
            'Data from our UX research department showed that 15% of users who started creating an account did not complete the process due to uncertainty about its duration',
          ]}
        />

        <ImageSlide 
          imageSlideImage={buxBenchmarkProgress}
          imageSlideTitle="Benchmarking other sign up processes"
          showTitle={true}       // Title is shown
          showImage={true}      // Image is hidden
          showCaption={false}     // Caption is shown
          showAnimation={false}
        />

         <TextSlide
          slideTitle="Patterns identified"
          slideText={[
            'Well defined sections',
            'Estimated time of completion',
            'Positive feedback on step completion',
          ]}
        />

        <ImageSlide 
          imageSlideImage={buxProgressInterstitial}
          imageSlideTitle="Progress indicators"
          showTitle={true}       // Title is shown
          showImage={true}      // Image is hidden
          showCaption={false}     // Caption is shown
          showAnimation={false}
        />

        <ImageSlide 
          imageSlideImage={buxProgressReturn}
          imageSlideTitle="To close the loop, we needed to make it easy for users to resume their registration"
          showTitle={true}       // Title is shown
          showImage={true}      // Image is hidden
          showCaption={false}     // Caption is shown
          showAnimation={false}
        />

        <TextSlide
          slideTitle="Results"
          slideText={[
            '~8% increase in same month account creation',
          ]}
        />

      </div>

      

      {/* Section 04 */}

      <div id="web-onboarding">
        <SectionCover sectionNumber="4" sectionTitle="Web onboarding" />

        

        <TextSlide
          slideTitle="Why?"
          slideText={[
            'Average of 55k monthly visits on the product’s website that could be converted more efficiently into sign-ups',
            'Overcome Apple and Google changes in which we wouldn’t be able to effectively track users activity',
            'Allow us to open a new marketing channel using affiliate websites',
          ]}
        />

        <ImageSlide 
          imageSlideTitle="Breakdown of the account creation flow"
          imageSlideImage={buxWebOnboardingFlow}
          showTitle={true}       // Title is shown
          showImage={true}      // Image is hidden
          showCaption={false}     // Caption is shown
          showAnimation={false}
        />

        <TextSlide
          slideTitle="Scope of MVP"
          slideText={[
            'Shared backend with mobile flow, meaning no changes in the backend',
            'Sign-up only - the minimum necessary to start marketing and measure success',
            'Redirect users to continue registration in the app',
          ]}
        />

        <ImageSlide 
          imageSlideTitle="Quick prototyping with already designed mobile components allowed us to catch problems early"
          imageSlideImage={buxWebOnboardingPrototype}
          showTitle={true}       // Title is shown
          showImage={true}      // Image is hidden
          showCaption={false}     // Caption is shown
          showAnimation={false}
        />
        
        <ImageSlide 
          imageSlideTitle="Wireframes to translate mobile content to web-desktop, for web-mobile we followed the same layout we already had"
          imageSlideImage={buxWebOnboardingWireframes}
          showTitle={true}       // Title is shown
          showImage={true}      // Image is hidden
          showCaption={false}     // Caption is shown
          showAnimation={false}
        />

        <ImageSlide 
          imageSlideTitle="Finally we needed a temporary way to redirect users to complete their account creation in the app "
          imageSlideImage={buxWebOnboardingQrCode}
          showTitle={true}       // Title is shown
          showImage={true}      // Image is hidden
          showCaption={false}     // Caption is shown
          showAnimation={false}
        />

        <ImageSlide 
          imageSlideTitle="Finally we needed a temporary way to redirect users to complete their account creation in the app "
          imageSlideImage={buxWebOnboardingLibrary}
          showTitle={true}       // Title is shown
          showImage={true}      // Image is hidden
          showCaption={false}     // Caption is shown
          showAnimation={false}
        />

        <ImageSlide 
          imageSlideTitle="Final product"
          imageSlideImage={buxWebOnboardingFinalProduct}
          imageSlideCaption="register.getbux.com"
          showTitle={true}       // Title is shown
          showImage={true}      // Image is hidden
          showCaption={true}     // Caption is shown
          showAnimation={false}
        />

        <TextSlide
          slideTitle="Results"
          slideText={[
            'Opened a new possibility for marketing with affiliate websites',
            'In the first 3 months 6% of all registrations were coming from web',
          ]}
          style={{borderColor: 'green'}}
        />

      </div>
      
      <div className="project-header">
        <h2>Thanks!</h2>
        <div className="project-header-image-bux"></div>
      </div>
      



      {/* Floating Navigation */}
      {showFloatingButtons && (
        <FloatingNavigation
          currentSectionIndex={currentSectionIndex}
          totalSections={sections.length}
          onPrevious={() => {
            if (currentSectionIndex > 0) {
              scrollToElement(`#${sections[currentSectionIndex - 1]?.id}`);
            }
          }}
          onNext={() => {
            if (currentSectionIndex < sections.length - 1) {
              scrollToElement(`#${sections[currentSectionIndex + 1]?.id}`);
            }
          }}
          onHome={() => scrollToElement('.project-index')}
        />
      )}
    </div>
  );
};

export default ProjectBuxZero;
